import { addMinutes, format, getDate, isEqual, parse, parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { ApiTimeZone } from 'src/models/api-configuration/api-configuration.model';

const dateFormat = 'PP';
const timeFormat = 'h:mm aaa';
const dateTimeFormat = `${dateFormat} - ${timeFormat}`;
const dateOnlyParse = 'yyyy-MM-dd';
const endsWithHack = 'T00:00:00+00:00';

interface FormatDateOptions {
    timeZone?: ApiTimeZone;
    showTime?: boolean;
    useErsDateOnlyHack?: boolean;
}

const getIsDateOnly = (date: string, options: FormatDateOptions) => {
    // look. ERS experiences start and end dates are "datetimes" but they are serialized
    // as DateOnly
    // The SDK generated by ERS generates DateTimeOffset
    // CareerHub's JSON converters assume UTC and stick this 'T00:00:00+00:00' on the end
    if (options?.useErsDateOnlyHack && date.endsWith(endsWithHack)) {
        return true;
    }

    // probably not the most accurate but good enough
    return date.length === dateOnlyParse.length;
};

const customParse = (date: string, options: FormatDateOptions) => {
    const isDateOnly = getIsDateOnly(date, options);

    if (isDateOnly) {
        return parse(date.replace(endsWithHack, ''), dateOnlyParse, new Date());
    }

    const parsed = parseISO(date);

    if (options?.timeZone?.ianaId) {
        return utcToZonedTime(parsed, options.timeZone.ianaId);
    }

    return options?.timeZone?.utcOffset
        ? addMinutes(parsed, parsed.getTimezoneOffset() + options.timeZone.utcOffset)
        : parsed;
};
const getFormattedDate = (date: string, options: FormatDateOptions) => {
    const isDateOnly = getIsDateOnly(date, options);
    const dateObj = customParse(date, options);

    if (isDateOnly) {
        return format(dateObj, dateFormat);
    }

    return format(dateObj, options?.showTime ? dateTimeFormat : dateFormat);
};

const getFormattedEndDate = (
    date: string,
    endDate: string | undefined,
    options: FormatDateOptions
): string | undefined => {
    if (!endDate) {
        return undefined;
    }

    const dateObj = customParse(date, options);
    const isDateOnly = getIsDateOnly(date, options);

    const endDateObj = customParse(endDate, options);
    const isSameDay = getDate(dateObj) === getDate(endDateObj);
    const isSameDayAndTime = isEqual(dateObj, endDateObj);

    if (isSameDayAndTime) {
        return undefined;
    }

    if (isSameDay) {
        return options?.showTime ? format(endDateObj, timeFormat) : undefined;
    }

    if (isDateOnly) {
        return format(endDateObj, dateFormat);
    }

    return format(endDateObj, options?.showTime ? dateTimeFormat : dateFormat);
};

export const getFormattedStartEnd = (
    date: string,
    endDate: string | undefined,
    options?: FormatDateOptions
) => {
    return {
        formattedStartDate: getFormattedDate(date, options || {}),
        formattedEndDate: getFormattedEndDate(date, endDate, options || {}),
    };
};
